import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, FileText, ScanSearch, Receipt, LogOut, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import DigestSettings from "@/components/account/DigestSettings";

const OPERATOR_ROLES = ["super_admin", "admin", "agency"];

const STATUS_CLS = {
  DRAFT: "bg-slate-100 text-slate-600", READY: "bg-sky-100 text-sky-700",
  SENT: "bg-indigo-100 text-indigo-700", VIEWED: "bg-violet-100 text-violet-700",
  ACCEPTED: "bg-emerald-100 text-emerald-700", DECLINED: "bg-red-100 text-red-700",
  EXPIRED: "bg-amber-100 text-amber-700",
};

const PAY_CLS = {
  paid: "bg-emerald-100 text-emerald-700", partially_refunded: "bg-amber-100 text-amber-700",
  refunded: "bg-red-100 text-red-700", failed: "bg-red-100 text-red-700",
};

const REFUND_CLS = {
  REQUESTED: "bg-sky-100 text-sky-700", PROCESSING: "bg-indigo-100 text-indigo-700",
  REFUNDED: "bg-red-100 text-red-700", PARTIALLY_REFUNDED: "bg-amber-100 text-amber-700",
  DENIED: "bg-slate-200 text-slate-600", FAILED: "bg-red-100 text-red-700",
};

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [refundFor, setRefundFor] = useState(null); // order object
  const [refundForm, setRefundForm] = useState({ full: true, amount: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/account/overview").then(({ data }) => setData(data))
    .catch(() => setData({ scans: [], proposals: [], orders: [], refunds: [] }));

  useEffect(() => { load(); }, []);

  if (!data) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const openRefundFor = (sess) =>
    (data.refunds || []).some((r) => r.order_session_id === sess && ["REQUESTED", "PROCESSING"].includes(r.status));

  const submitRefund = async () => {
    if (!refundFor) return;
    if (!refundForm.reason.trim() || refundForm.reason.trim().length < 3) { toast.error("Please add a reason."); return; }
    setBusy(true);
    try {
      const body = { order_session_id: refundFor.session_id, reason: refundForm.reason.trim() };
      if (!refundForm.full) {
        const amt = Number(refundForm.amount);
        if (!amt || amt <= 0) { toast.error("Enter a valid amount."); setBusy(false); return; }
        body.amount = amt;
      }
      await api.post("/refunds", body);
      toast.success("Refund request submitted. We'll review it shortly.");
      setRefundFor(null);
      setRefundForm({ full: true, amount: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="account-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Your account</h1>
          <p className="text-slate-500 mt-1">{user?.name} · {user?.email}</p>
        </div>
        <Button variant="outline" onClick={async () => { await logout(); nav("/"); }} data-testid="account-logout">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>

      {OPERATOR_ROLES.includes(user?.role) && <DigestSettings />}

      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" /> Proposals</h2>
          <Link to="/proposals" className="text-sm text-indigo-600 font-medium" data-testid="account-all-proposals">View all</Link>
        </div>
        <div className="mt-3 space-y-2" data-testid="account-proposals">
          {(data.proposals || []).length === 0 && <p className="text-sm text-slate-400">No proposals yet. Run a scan and build one from the findings.</p>}
          {(data.proposals || []).slice(0, 6).map((p) => (
            <Link to={`/proposals/${p.public_id}`} key={p.public_id} data-testid={`account-proposal-${p.public_id}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-300 transition-colors">
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate">{p.title}</p>
                <p className="text-xs text-slate-500">{p.business_name || "—"}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-semibold text-slate-900">{fmtMoney(p?.totals?.one_time?.client_price, p?.totals?.currency)}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_CLS[p.status] || "bg-slate-100 text-slate-600"}`}>{p.status}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><ScanSearch className="w-4 h-4 text-indigo-600" /> Your scans</h2>
        <div className="mt-3 space-y-2" data-testid="account-scans">
          {(data.scans || []).length === 0 && <p className="text-sm text-slate-400">No saved scans on this account yet.</p>}
          {(data.scans || []).slice(0, 6).map((s) => (
            <Link to={`/report/${s.public_id}`} key={s.public_id}
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-300 transition-colors">
              <div className="min-w-0"><p className="font-medium text-slate-900 truncate">{s?.business?.name || "Business"}</p>
                <p className="text-xs text-slate-500">{s?.business?.city} · {s.status}</p></div>
              {s?.report?.health_score != null && <span className="font-semibold text-slate-900">{s.report.health_score}/100</span>}
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><Receipt className="w-4 h-4 text-indigo-600" /> Orders</h2>
        <div className="mt-3 space-y-2" data-testid="account-orders">
          {(data.orders || []).length === 0 && <p className="text-sm text-slate-400">No orders yet.</p>}
          {(data.orders || []).map((o) => {
            const cur = (o.currency || "usd").toUpperCase();
            const canRefund = ["paid", "partially_refunded"].includes(o.payment_status) && !openRefundFor(o.session_id);
            return (
              <div key={o.session_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`order-${o.session_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{o.business_name || o.proposal_id} · {o.label}</p>
                  <p className="text-xs text-slate-500">{(o.created_at || "").slice(0, 10)}{o.refunded_amount ? ` · ${fmtMoney(o.refunded_amount, cur)} refunded` : ""}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-semibold text-slate-900">{fmtMoney(o.amount, cur)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PAY_CLS[o.payment_status] || "bg-slate-100 text-slate-600"}`}>{(o.payment_status || "").replace("_", " ")}</span>
                  {canRefund && (
                    <Button size="sm" variant="outline" data-testid={`request-refund-${o.session_id}`}
                      onClick={() => { setRefundFor(o); setRefundForm({ full: true, amount: "", reason: "" }); }}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Refund
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {(data.refunds || []).length > 0 && (
        <section className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><RotateCcw className="w-4 h-4 text-indigo-600" /> Refund requests</h2>
          <div className="mt-3 space-y-2" data-testid="account-refunds">
            {data.refunds.map((r) => (
              <div key={r.public_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`refund-${r.public_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{fmtMoney(r.status === "PARTIALLY_REFUNDED" || r.status === "REFUNDED" ? r.refunded_amount : r.amount_requested, (r.currency || "usd").toUpperCase())} · {r.reason}</p>
                  <p className="text-xs text-slate-500">Requested {(r.created_at || "").slice(0, 10)}{r.resolved_at ? ` · resolved ${(r.resolved_at).slice(0, 10)}` : ""}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${REFUND_CLS[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <Dialog open={!!refundFor} onOpenChange={(o) => !o && setRefundFor(null)}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Request a refund</DialogTitle>
            <DialogDescription>
              Your request will be reviewed by our team before any money is returned. Refunds follow our{" "}
              <Link to="/refund-policy" className="text-indigo-600 underline" target="_blank">Refund &amp; Cancellation Policy</Link>.
            </DialogDescription>
          </DialogHeader>
          {refundFor && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Order: <b>{fmtMoney(refundFor.amount, (refundFor.currency || "usd").toUpperCase())}</b> · {refundFor.business_name || refundFor.label}</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setRefundForm((f) => ({ ...f, full: true }))} data-testid="refund-full"
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${refundForm.full ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>Full amount</button>
                <button type="button" onClick={() => setRefundForm((f) => ({ ...f, full: false }))} data-testid="refund-partial"
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${!refundForm.full ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>Partial</button>
              </div>
              {!refundForm.full && (
                <Input type="number" min="1" placeholder="Amount to refund" value={refundForm.amount}
                  onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))} data-testid="refund-amount" />
              )}
              <textarea rows={3} placeholder="Reason for your refund request" value={refundForm.reason}
                onChange={(e) => setRefundForm((f) => ({ ...f, reason: e.target.value }))} data-testid="refund-reason"
                className="w-full rounded-lg border border-slate-200 p-2 text-sm" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundFor(null)} data-testid="refund-cancel">Cancel</Button>
            <Button onClick={submitRefund} disabled={busy} data-testid="refund-submit" className="bg-slate-900 hover:bg-slate-800">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
